import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import moment from 'moment';
import { Checkbox } from '@cargomatic/ui';
import { updateLaneAccessorialRates } from 'utils/AccessorialRequestWebAPIUtils';
import CargomaticInput from 'components/v2/input';
import CargomaticModal from 'components/v2/modal';
import LoadingCircle from 'components/ui/LoadingCircle';
import Alert from 'components/ui/Alert';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import Tabs from 'components/v2/navs/Tabs';
import { startCase } from 'lodash';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Label from 'components/v2/forms/Label';
import Badge from 'components/v2/Badge';
import CargomaticDropzone from 'components/v2/DropZone';
import Tooltip from 'components/v2/Tooltip';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { ACCESSORIAL_RATE_FIELDS, ACTION, BLANK_ACCESSORIAL_RATE, STATUS, } from 'constants/AccessorialRates';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { showToast } from 'stores/ToastStore';
import AddButton from '../../../v2/buttons/AddButton';
import CargomaticSelect from '../../../v2/select';
import IconTextDisplay from '../../../v2/IconTextDisplay';
import CalendarIcon from '../../../v2/icons/CalendarIcon';
import AppointmentWindowModal from './LaneRatesAppointmentWindowModal';
import InfoIcon from 'components/v2/icons/InfoIcon';
import { createLaneAccessorialRatesPayload, fetchAccessorials, fetchExistingAccessorialRates, fetchStandardAccessorialRates, } from './helpers/accessorial-rate-helpers';
const FORMATS = [
    { name: '$', value: '$', localizationSlug: 'DOLLAR' },
    {
        name: '% of line haul rate',
        value: '%',
        localizationSlug: 'PERCENT_OF_LANE_HAUL_RATE',
    },
];
const UNITS = [
    { name: 'Per Event', value: 'per event', localizationSlug: 'PER_EVENT' },
    { name: 'Per Day', value: 'per day', localizationSlug: 'PER_DAY' },
];
const DATE_TIME_FORMAT = 'L';
function renderInfoIcon() {
    return React.createElement(InfoIcon, null);
}
export default function AddAccessorialRatesModal({ showModal, onClose, onBack, handleSubmit, handleDropFiles, uploadingFiles, disableButtons, loadingConfirmation, customerData, market, }) {
    const [showAccessorialRatesAppointmentModal, setShowAccessorialRatesAppointmentModal,] = useState({ show: false, index: null });
    const [showAlreadyConfirmedModal, setShowAlreadyConfirmedModal] = useState(false);
    const [date, setDate] = useState({
        effectiveStart: null,
        effectiveEnd: null,
    });
    const [accessorials, setAccessorials] = useState([]);
    const [newAccessorialRate, setNewAccessorialRate] = useState(BLANK_ACCESSORIAL_RATE);
    const [laneAccessorialRates, setLaneAccessorialRates] = useState([]);
    const [deleteError, setDeleteError] = useState(false);
    const [areDatesValid, setAreDatesValid] = useState(false);
    const [duplicateAccessorial, setDuplicateAccessorial] = useState(false);
    const [allAccessorialRatesValid, setAllAccessorialLanesValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFetchingAccessorials, setIsFetchingAccessorials] = useState(false);
    const [isFetchingAccessorialRates, setIsFetchingAccessorialRates] = useState(false);
    const [doCustomersHaveFiles, setDoCustomersHaveFiles] = useState(true);
    const [isEdited, setIsEdited] = useState(false);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(disableButtons);
    const [laneAccessorialRatesPayload, setLaneAccessorialRatesPayload] = useState([]);
    const { t } = useTranslation();
    const LOADING_TEXT = t('LOADING');
    const formatDate = (date) => {
        if (!date) {
            return t('NOT_SET');
        }
        return moment(date).format(DATE_TIME_FORMAT);
    };
    const TABS = [
        {
            name: startCase(`${t('USER', { count: 2 })}`),
        },
        {
            name: startCase(`${t('ACCESSORIAL_RATES', { count: 2 })}`),
        },
    ];
    // validators
    const validateDuplicateAccessorialRates = (accessorialRates) => {
        const cache = {};
        for (let i = 0; i < accessorialRates.length; i++) {
            const currAccessorial = accessorialRates[i].accessorialtype;
            if (currAccessorial) {
                if (cache[currAccessorial]) {
                    setDuplicateAccessorial(true);
                    return;
                }
                cache[currAccessorial] = true;
            }
        }
        setDuplicateAccessorial(false);
    };
    const validateDates = (startDate, endDate) => {
        if (!endDate || !startDate || endDate <= startDate) {
            setAreDatesValid(false);
            return false;
        }
        setAreDatesValid(true);
        return true;
    };
    const validateCustomerDataFiles = (customerData) => {
        let areFilesValid = true;
        if (isEdited) {
            const customersHaveFiles = customerData.every((customer) => Boolean(customer.files && customer.files.length));
            setDoCustomersHaveFiles(customersHaveFiles);
            areFilesValid = customersHaveFiles;
        }
        return areFilesValid;
    };
    const validateAccessorialRatesPayload = (laneAccessorialRatesPayload) => {
        const payloadHasValidInfo = laneAccessorialRatesPayload.every(
        // change this
        (accessorialRate) => {
            return (accessorialRate.customer &&
                accessorialRate.market &&
                accessorialRate.accessorialtype &&
                accessorialRate.rate > 0 &&
                accessorialRate.ratetype &&
                accessorialRate.units &&
                accessorialRate.effectiveStart &&
                accessorialRate.effectiveEnd);
        });
        validateDuplicateAccessorialRates(laneAccessorialRates);
        const areAccessorialRatesValid = payloadHasValidInfo && !duplicateAccessorial && areDatesValid;
        return areAccessorialRatesValid;
    };
    // useEffects
    useEffect(() => {
        const fetchData = async () => {
            setIsFetchingAccessorials(true);
            try {
                const accessorials = await fetchAccessorials();
                setAccessorials(accessorials);
            }
            catch (e) {
                showToast(t('REQUEST_ERROR'), t('LOADING_ACCESSORIALS_ERROR'), 'error');
            }
            finally {
                setIsFetchingAccessorials(false);
            }
        };
        fetchData();
    }, []);
    useEffect(() => {
        setAllAccessorialLanesValid(false);
        let laneAccessorialRates;
        const fetchAccessorialRatesData = async () => {
            setIsFetchingAccessorialRates(true);
            try {
                const { customer, customerType } = customerData[0];
                const existingAccessorialRates = await fetchExistingAccessorialRates({
                    status: STATUS.CONFIRMED,
                    market,
                    customer,
                });
                if (existingAccessorialRates && existingAccessorialRates.length) {
                    setDate({
                        effectiveStart: existingAccessorialRates[0].effectiveStart,
                        effectiveEnd: existingAccessorialRates[0].effectiveEnd,
                    });
                    laneAccessorialRates = existingAccessorialRates;
                }
                else {
                    const standardAccessorialRates = await fetchStandardAccessorialRates({
                        customer,
                        customerType,
                        market,
                    });
                    laneAccessorialRates = standardAccessorialRates;
                }
                laneAccessorialRates = laneAccessorialRates.map((accessorialRate) => {
                    return Object.assign({}, accessorialRate);
                });
                setLaneAccessorialRates(laneAccessorialRates);
                if (laneAccessorialRates && laneAccessorialRates.length) {
                    setIsEdited(true);
                }
            }
            catch (e) {
                showToast(t('REQUEST_ERROR'), t('LOADING_ACCESSORIALS_ERROR'), 'error');
            }
            finally {
                setIsFetchingAccessorialRates(false);
            }
        };
        fetchAccessorialRatesData();
    }, []);
    useEffect(() => {
        const laneAccessorialRatesPayload = createLaneAccessorialRatesPayload({
            customerData,
            date,
            market,
            laneAccessorialRates,
        });
        setLaneAccessorialRatesPayload(laneAccessorialRatesPayload);
    }, [date, customerData, market, laneAccessorialRates]);
    useEffect(() => {
        setAllAccessorialLanesValid(false);
        const isPayloadValid = validateAccessorialRatesPayload(laneAccessorialRatesPayload);
        setAllAccessorialLanesValid(isPayloadValid);
    }, [laneAccessorialRatesPayload]);
    useEffect(() => {
        validateDates(date.effectiveStart, date.effectiveEnd);
    }, [date]);
    useEffect(() => {
        validateCustomerDataFiles(customerData);
    }, [customerData]);
    useEffect(() => {
        setIsLoading(isDeleting ||
            isFetchingAccessorials ||
            isFetchingAccessorialRates ||
            uploadingFiles ||
            loadingConfirmation);
    }, [
        isDeleting,
        isFetchingAccessorials,
        isFetchingAccessorialRates,
        uploadingFiles,
        loadingConfirmation,
    ]);
    // handlers
    const handleAddNewAccessorialRate = () => {
        const currNewAccessorialRate = Object.assign({}, newAccessorialRate);
        currNewAccessorialRate.effectiveEnd = date.effectiveEnd;
        currNewAccessorialRate.effectiveStart = date.effectiveStart;
        setLaneAccessorialRates((prev) => {
            return [...prev, currNewAccessorialRate];
        });
        let defaultAccessorialRate = Object.assign({}, BLANK_ACCESSORIAL_RATE);
        const isShipper = checkUserType(customerData);
        if (!isShipper) {
            defaultAccessorialRate === null || defaultAccessorialRate === void 0 ? true : delete defaultAccessorialRate.autoApply;
        }
        setNewAccessorialRate(defaultAccessorialRate);
    };
    const handleNewAccessorialRateChange = (field, value) => {
        setNewAccessorialRate((previousNewAccessorialRate) => {
            return Object.assign(Object.assign({}, previousNewAccessorialRate), { [field]: value });
        });
    };
    const handleAccessorialRateAppointment = (index) => {
        setIsEdited(true);
        setShowAccessorialRatesAppointmentModal({ show: true, index });
    };
    const handleAccessorialRateFieldChange = (rate, index, field) => {
        let updatedItem = laneAccessorialRates[index];
        updatedItem[field] = rate;
        setIsEdited(true);
        setLaneAccessorialRates((prev) => {
            return [
                ...prev.slice(0, index),
                Object.assign({}, updatedItem),
                ...prev.slice(index + 1),
            ];
        });
    };
    const handleRemoveAccessorialRate = (index) => {
        setLaneAccessorialRates((prev) => {
            return [...prev.slice(0, index), ...prev.slice(index + 1)];
        });
    };
    const handleDeleteAccessorialRate = async (index) => {
        setIsDeleting(true);
        setDeleteError(false);
        const accessorialRate = laneAccessorialRates[index];
        try {
            if (accessorialRate.createdAt) {
                accessorialRate.effectiveEnd = moment()
                    .add(-1, 'day')
                    .endOf('day')
                    .utc()
                    .toISOString();
                accessorialRate.status = STATUS.EXPIRED;
                accessorialRate.alreadyConfirmed = true;
                await updateLaneAccessorialRates([accessorialRate], ACTION.REMOVE);
            }
            handleRemoveAccessorialRate(index);
            setIsDeleting(false);
        }
        catch (e) {
            setIsDeleting(false);
            setDeleteError(true);
        }
    };
    const handleClickSubmit = async () => {
        const validDates = validateDates(date.effectiveStart, date.effectiveEnd);
        const validAccRatesPayload = validateAccessorialRatesPayload(laneAccessorialRatesPayload);
        const validCustomerDataFiles = validateCustomerDataFiles(customerData);
        const isPayloadValid = validDates && validAccRatesPayload && validCustomerDataFiles;
        if (isPayloadValid) {
            await handleSubmit(laneAccessorialRatesPayload);
        }
    };
    // customer panel renderers
    const renderTableCustomerData = () => {
        return (React.createElement("table", { "data-testid": "table-a4cc14d3", className: "default-table" },
            React.createElement("thead", { "data-testid": "thead-36f90d48" },
                React.createElement("tr", { "data-testid": "tr-0351992f" },
                    React.createElement("th", { "data-testid": "th-8068141d" }, startCase(t('USER', { count: 2 }))),
                    React.createElement("th", { "data-testid": "th-4557855a" }, startCase(t('TYPE'))),
                    React.createElement("th", { "data-testid": "th-3d432250" }, startCase(t('UPLOAD'))))),
            React.createElement("tbody", { "data-testid": "tbody-0e2a25fa" }, renderTableRowsCustomerData(customerData))));
    };
    const renderTabPanelCustomers = () => {
        return (React.createElement(Tab.Panel, { "data-testid": "undefined-8caf1c50" },
            renderUserErrors(),
            renderTableCustomerData()));
    };
    const renderRate = ({ accessorialRate, index, isNew = false }) => {
        const onChangeTableRow = (value) => handleAccessorialRateFieldChange(value, index, ACCESSORIAL_RATE_FIELDS.RATE);
        const onChangeNewRate = (value) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.RATE, value);
        };
        const onChange = isNew ? onChangeNewRate : onChangeTableRow;
        return (React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-4b822246", value: accessorialRate.rate, type: "number", onChange: onChange, hideLabel: true }));
    };
    const renderQuoteId = ({ accessorialRate, index, isNew = false }) => {
        const onChangeTableRow = (value) => handleAccessorialRateFieldChange(value, index, ACCESSORIAL_RATE_FIELDS.QUOTE_ID);
        const onChangeNewRate = (value) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.QUOTE_ID, value);
        };
        const onChange = isNew ? onChangeNewRate : onChangeTableRow;
        return (React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-4b822246", value: accessorialRate.quoteId, type: "text", onChange: onChange, hideLabel: true }));
    };
    const renderNotes = ({ accessorialRate, index, isNew = false }) => {
        const onChangeTableRow = (value) => handleAccessorialRateFieldChange(value, index, ACCESSORIAL_RATE_FIELDS.NOTES);
        const onChangeNewRate = (value) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.NOTES, value);
        };
        const onChange = isNew ? onChangeNewRate : onChangeTableRow;
        return (React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-4b822246", value: accessorialRate.notes, type: "text", onChange: onChange, hideLabel: true }));
    };
    const renderValidityDates = (date) => {
        return (React.createElement("div", { "data-testid": "div-cb4d7737", className: "space-y-2" },
            React.createElement("button", { "data-testid": "button-66b0fb33", onClick: () => handleAccessorialRateAppointment(0), className: "flex dates-center" },
                React.createElement(IconTextDisplay, { dataTestId: "IconTextDisplay-144a2902", color: "green", icon: React.createElement(CalendarIcon, null), className: "w-36" },
                    React.createElement("dl", { "data-testid": "dl-994ea201", className: "text-left" },
                        React.createElement("dt", { "data-testid": "dt-4f6ddfb5", className: "mb-1 form-label" }, startCase(t('START'))),
                        React.createElement("dd", { "data-testid": "dd-c2db849e", className: "block text-sm text-gray-900" }, formatDate(date.effectiveStart)))),
                React.createElement(IconTextDisplay, { dataTestId: "IconTextDisplay-db79e3d9", color: "red", icon: React.createElement(CalendarIcon, null), className: "ml-2" },
                    React.createElement("dl", { "data-testid": "dl-defa89b7", className: "text-left" },
                        React.createElement("dt", { "data-testid": "dt-3edbfa66", className: "mb-1 form-label" }, startCase(t('END'))),
                        React.createElement("dd", { "data-testid": "dd-02cebb8d", className: "block text-sm text-gray-900" }, formatDate(date.effectiveEnd))))),
            React.createElement(AppointmentWindowModal, { show: showAccessorialRatesAppointmentModal.show &&
                    showAccessorialRatesAppointmentModal.index === 0, onClose: hideAccessorialRateAppointment, validity: date, onStartChange: (start) => {
                    const newDate = start.toISOString();
                    setDate((prev) => {
                        const dateState = Object.assign(Object.assign({}, prev), { effectiveStart: newDate });
                        return dateState;
                    });
                }, onEndChange: (end) => {
                    const newDate = end.toISOString();
                    setDate((prev) => (Object.assign(Object.assign({}, prev), { effectiveEnd: newDate })));
                } })));
    };
    const renderAccessorialRateType = ({ accessorialRate, index, isNew = false, }) => {
        const onSelectTableRow = (value) => {
            handleAccessorialRateFieldChange(value.name, index, ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_TYPE);
            handleAccessorialRateFieldChange(value.charge_code, index, ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_CODE);
        };
        const onSelectNewRate = (accessorial) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_TYPE, accessorial.name);
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_CODE, accessorial.charge_code);
        };
        const onSelect = isNew ? onSelectNewRate : onSelectTableRow;
        return (React.createElement("div", { "data-testid": "div-4ea75dbb" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-ef8a8ee4", items: accessorials, onSelect: onSelect, value: accessorialRate.accessorialtype, textKey: "name", valueKey: "name", placeholder: startCase(t('TYPE')), hideLabel: true })));
    };
    const renderTableRowDocumentUpload = (index) => {
        return (React.createElement(CargomaticDropzone, { dataTestId: "CargomaticDropzone-9f4511f1", onDrop: handleDropFiles(index), style: { padding: 0, borderWidth: 0, borderRadius: 0 }, multiple: false, button: true, acceptedFiles: ACCEPTED_FILE_TYPES, disabled: isLoading, dropzoneMessage: "", showIcon: true, color: "green" }));
    };
    const renderTableRowsCustomerData = (customerData) => {
        return customerData.map((customer, i) => {
            return (React.createElement("tr", { "data-testid": `tr-e25f73df-${i}`, key: `customer-data-${i}` },
                React.createElement("td", { "data-testid": `td-3ef66338-${i}` }, customer.name),
                React.createElement("td", { "data-testid": `td-c2fcfb7c-${i}` }, customer.customerType === 'carrier' ? (React.createElement(Badge, { dataTestId: `Badge-96389945-${i}`, color: "blue", size: "large", rounded: true }, startCase(t('CARRIER')))) : (React.createElement(Badge, { dataTestId: `Badge-b92aa224-${i}`, color: "green", size: "large", rounded: true }, startCase(t('SHIPPER'))))),
                React.createElement("td", { "data-testid": `td-adc14164-${i}` }, renderTableRowDocumentUpload(i))));
        });
    };
    const checkUserType = (customerData = [], customerType = "shipper") => {
        if (!(customerData === null || customerData === void 0 ? void 0 : customerData.length))
            return false;
        return !!customerData.find((customer) => (customer === null || customer === void 0 ? void 0 : customer.customerType) === customerType);
    };
    const isAutoApplyFeatureAllowed = (customerData = [], customerType = "shipper") => {
        const isFeatureEnabled = featureEnabled(CONFIGURED_FEATURES === null || CONFIGURED_FEATURES === void 0 ? void 0 : CONFIGURED_FEATURES.AUTO_APPLY_ACCESSORIAL);
        const isValidUserType = checkUserType(customerData, customerType);
        return (isFeatureEnabled && isValidUserType);
    };
    // accessorial rates panel renderers
    const renderTableRowAccessorialRates = (laneAccessorialRates) => {
        const showCheckbox = isAutoApplyFeatureAllowed(customerData);
        return laneAccessorialRates.map((accessorialRate, index) => {
            return (React.createElement("tr", { "data-testid": `tr-71cc8353-${index}`, key: `accessorial-rates-${index}` },
                React.createElement("td", { "data-testid": `td-79ca190f-${index}` }, accessorialRate.createdAt
                    ? accessorialRate.accessorialtype
                    : renderAccessorialRateType({ accessorialRate, index })),
                React.createElement("td", { "data-testid": `td-6df7b874-${index}` }, accessorialRate.accesscode),
                React.createElement("td", { "data-testid": `td-8d1b8413-${index}` }, renderRateType({ accessorialRate, index })),
                React.createElement("td", { "data-testid": `td-2a2fbec8-${index}` }, renderRate({ accessorialRate, index })),
                React.createElement("td", { "data-testid": `td-acfd94d6-${index}` }, renderUnit({ accessorialRate, index })),
                showCheckbox && (React.createElement("td", { "data-testid": `td-acfd94d7-${index}` }, renderCheckbox({ accessorialRate, index }))),
                React.createElement("td", { "data-testid": `td-7a4h9ec8-${index}` }, renderQuoteId({ accessorialRate, index })),
                React.createElement("td", { "data-testid": `td-2c1fbac8-${index}` }, renderNotes({ accessorialRate, index })),
                React.createElement("td", { "data-testid": `td-ef977cb4-${index}` },
                    React.createElement("span", { "data-testid": `span-98ded7ca-${index}`, className: "sr-only" }, t('DELETE')),
                    React.createElement(DeleteButton, { dataTestId: `DeleteButton-e48e77ff-${index}`, disabled: isLoading, onClick: () => handleDeleteAccessorialRate(index) }))));
        });
    };
    const renderTableFooterNewAccessorialRate = () => {
        const isNew = true;
        const accessorialRate = newAccessorialRate;
        const showCheckbox = isAutoApplyFeatureAllowed(customerData);
        return (React.createElement("tr", { "data-testid": "tr-a8732627", key: "blank-accessorial-rate" },
            React.createElement("td", { "data-testid": "td-d6c5d7c9" }, renderAccessorialRateType({
                accessorialRate,
                isNew,
            })),
            React.createElement("td", { "data-testid": "td-b319bcf0" }, accessorialRate.accesscode),
            React.createElement("td", { "data-testid": "td-c7b8aabb" }, renderRateType({ accessorialRate, isNew })),
            React.createElement("td", { "data-testid": "td-72e1e416" }, renderRate({ accessorialRate, isNew })),
            React.createElement("td", { "data-testid": "td-59ef1055" }, renderUnit({ accessorialRate, isNew })),
            showCheckbox && (React.createElement("td", { "data-testid": "td-59ef1235" }, renderCheckbox({ accessorialRate, isNew }))),
            React.createElement("td", { "data-testid": "td-71e5e435" }, renderQuoteId({ accessorialRate, isNew })),
            React.createElement("td", { "data-testid": "td-57ef1265" }, renderNotes({ accessorialRate, isNew })),
            React.createElement("td", { "data-testid": "td-444a4650" },
                React.createElement("span", { "data-testid": "span-cf938201", className: "sr-only" }, t('DELETE')),
                React.createElement(AddButton, { dataTestId: "AddButton-7ece27de", disabled: isLoading, onClick: () => handleAddNewAccessorialRate() }))));
    };
    const renderTableAccessorialRates = (laneAccessorialRates) => {
        const showCheckbox = isAutoApplyFeatureAllowed(customerData);
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { "data-testid": "div-c1564417", className: "mb-4" },
                React.createElement(Label, { dataTestId: "Label-cc7db822", className: "mb-1" }, "Validatity Date Range"),
                React.createElement("div", { "data-testid": "div-d87dd869", className: "grid grid-cols-2 gap-4" }, renderValidityDates(date))),
            React.createElement("table", { "data-testid": "table-b260ffd4", className: "default-table" },
                React.createElement("thead", { "data-testid": "thead-5644abe7" },
                    React.createElement("tr", { "data-testid": "tr-6a462845" },
                        React.createElement("th", { "data-testid": "th-1cde1d02" }, t('ACCESSORIAL_TYPE')),
                        React.createElement("th", { "data-testid": "th-649bc73b" }, t('ACCESSORIAL_CODE')),
                        React.createElement("th", { "data-testid": "th-5e8fa0e5" }, t('DOLLAR_PERCENTAGE')),
                        React.createElement("th", { "data-testid": "th-a7cb8678" }, startCase(t('RATE', { count: 1 }))),
                        React.createElement("th", { "data-testid": "th-3e0820bb" }, startCase(t('UNIT', { count: 1 }))),
                        showCheckbox && (React.createElement("th", { "data-testid": "th-3e0820zs", className: 'flex items-center gap-2' },
                            startCase(t('AUTO_APPLY')),
                            "?",
                            React.createElement(Tooltip, { dataTestId: "Tooltip-auto-apply-ff3e64e3", id: "auto-apply-checkbox-info", body: renderInfoIcon, position: "top" },
                                React.createElement("div", { "data-testid": "div-auto-apply-9b70cbbc", style: { fontSize: '10px' } }, t('AUTO_APPLY_NOTE'))))),
                        React.createElement("th", { "data-testid": "th-619bc25b" }, t('QUOTE_ID')),
                        React.createElement("th", { "data-testid": "th-5e7fa1e4" }, t('NOTES')),
                        React.createElement("th", { "data-testid": "th-1b09bf72" }))),
                React.createElement("tbody", { "data-testid": "tbody-24c8a083" }, renderTableRowAccessorialRates(laneAccessorialRates)),
                React.createElement("tfoot", { "data-testid": "tfoot-57f30956" }, renderTableFooterNewAccessorialRate()))));
    };
    const renderTabPanelAccessorialRates = () => {
        return (React.createElement(Tab.Panel, { "data-testid": "undefined-255f1193" },
            renderAccessorialRatesErrors(),
            renderTableAccessorialRates(laneAccessorialRates)));
    };
    const renderRateType = ({ accessorialRate, index, isNew = false }) => {
        const onSelectTableRow = (value) => {
            handleAccessorialRateFieldChange(value.value, index, ACCESSORIAL_RATE_FIELDS.RATE_TYPE);
        };
        const onSelectNewRate = (value) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.RATE_TYPE, value.value);
        };
        const onSelect = isNew ? onSelectNewRate : onSelectTableRow;
        return (React.createElement("div", { "data-testid": "div-a91fb1d0" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-071ba467", items: FORMATS, onSelect: onSelect, value: accessorialRate.ratetype, textKey: "localizationSlug", valueKey: "value", placeholder: t('DOLLAR_PERCENTAGE'), hideLabel: true })));
    };
    const renderCheckbox = ({ accessorialRate, index, isNew = false }) => {
        const onSelectTableRow = (value) => {
            handleAccessorialRateFieldChange(value, index, ACCESSORIAL_RATE_FIELDS.AUTO_APPLY);
        };
        const onSelectNewRate = (value) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.AUTO_APPLY, value);
        };
        const onCheckedChange = isNew ? onSelectNewRate : onSelectTableRow;
        return (React.createElement(Checkbox, { className: "cursor-pointer", id: `auto-apply-checkbox-${index}`, defaultChecked: false, onCheckedChange: onCheckedChange, checked: (accessorialRate === null || accessorialRate === void 0 ? void 0 : accessorialRate.autoApply) || false }));
    };
    const renderUnit = ({ accessorialRate, index, isNew = false }) => {
        const onSelectTableRow = (value) => {
            handleAccessorialRateFieldChange(value.value, index, ACCESSORIAL_RATE_FIELDS.UNIT);
        };
        const onSelectNewRate = (value) => {
            handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.UNIT, value.value);
        };
        const onSelect = isNew ? onSelectNewRate : onSelectTableRow;
        return (React.createElement("div", { "data-testid": "div-25543025" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-4c057b0b", items: UNITS, onSelect: onSelect, value: accessorialRate.units, textKey: "name", valueKey: "value", placeholder: startCase(t('UNIT', { count: 1 })), hideLabel: true })));
    };
    const renderAccessorialRatesErrors = () => {
        return (React.createElement("div", { "data-testid": "div-bb35a60b", className: "mb-2 my-4" },
            !areDatesValid && (React.createElement(Alert, { dataTestId: "Alert-e236212d", className: "mb-2", type: "error" }, t('INVALID_END_DATE'))),
            duplicateAccessorial && (React.createElement(Alert, { dataTestId: "Alert-cb03a2ec", className: "mb-2", type: "error" }, t('ACCESSORIAL_RATE_EXIST'))),
            deleteError && (React.createElement(Alert, { dataTestId: "Alert-7e1422e0", className: "mb-2", type: "error" }, t('ACCESSORIAL_RATE_REMOVE_FAILED')))));
    };
    const renderUserErrors = () => {
        return (React.createElement("div", { "data-testid": "div-11823efc", className: "mb-2 my-4" }, !doCustomersHaveFiles && (React.createElement(Alert, { dataTestId: "Alert-56abb3dd", className: "mb-2", type: "error" }, t('CUSTOMERS_MISSING_REQUIRED_FILES_ERROR')))));
    };
    const getBody = () => {
        if (isFetchingAccessorialRates) {
            return (React.createElement("div", { "data-testid": "div-8e0adb54", className: "flex justify-center p-14" },
                React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-3cb88034", text: t('LOADING_ACCESSORIALS') })));
        }
        return (React.createElement("div", { "data-testid": "div-ded2fe8b", className: "ml-2 relative" },
            React.createElement("div", { "data-testid": "div-44c87a31", className: "flex items-center space-x-2 mb-4" },
                React.createElement("div", { "data-testid": "div-2d242038", className: "px-4 py-4" },
                    React.createElement("h3", { "data-testid": "h3-e7d09bc2", className: "text-base font-semibold leading-6 text-gray-900", id: "headlessui-dialog-title-:r2:", "data-headlessui-state": "open" }, t('STEP_2_ENTER_ACCESSORIAL_RATE'))),
                isLoading && (React.createElement("div", { "data-testid": "div-01a04f63", className: "p-4" },
                    React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-fbcd0547", text: LOADING_TEXT })))),
            React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-a0fae186", label: startCase(t('MARKET', { count: 1 })), className: "mb-4" }, market),
            React.createElement(Tabs, { dataTestId: "Tabs-8bc8b047", tabs: TABS },
                renderTabPanelCustomers(),
                renderTabPanelAccessorialRates())));
    };
    const getFooter = () => {
        return (React.createElement("div", { "data-testid": "div-95657805", className: "flex items-center justify-end space-x-4" },
            React.createElement("button", { "data-testid": "button-8750a0b3", className: "button button-secondary", onClick: onBack }, startCase(`${t('BACK')}`)),
            React.createElement("button", { "data-testid": "button-9cccef1a", className: "button button-green", onClick: handleClickSubmit, disabled: disableButtons ||
                    !allAccessorialRatesValid ||
                    isLoading ||
                    !doCustomersHaveFiles }, startCase(`${t('SUBMIT')}`))));
    };
    const hideAccessorialRateAppointment = () => {
        setShowAccessorialRatesAppointmentModal({ show: false, index: null });
    };
    return (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-9b085936", show: showModal, onHide: () => onClose(true), body: getBody(), dialogClassName: "rounded-xl border-4 border-green-cm w-5/6", showCloseButton: false, showHeader: false, footer: getFooter() }));
}
